import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { currencySymbol } from "@/lib/currency";
import { useGradeBands } from "@/hooks/useValueCatalog";
import { daysUntil } from "@/lib/offeringCardVisual";
import { OFFERING_TYPE_THEME } from "@/lib/offeringTypeTheme";
import { useAuth } from "@/hooks/use-auth-compat";
import { getOfferingMatchScore } from "@/services/offeringSearchService";
import type { OfferingSearchResult, OfferingType } from "@/services/offeringSearchService";
import type { MatchScoreBreakdown } from "@/services/marketplaceSearchService";
import type { MatchScore, MatchBand } from "@/types/matchScore";
import type { SearchContextSnapshot } from "@/types/searchResults";
import { WhyThisMatch } from "@/components/match/WhyThisMatch";
import { MATCH_SCORE_UI_ENABLED } from "@/config/featureFlags";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { SubscriptionTierBadge } from "@/components/teacher/SubscriptionTierBadge";
import { RatingBadge } from "@/components/reviews/RatingBadge";

/**
 * Offering-centric result card (ADR-088 AC8). A white card with the
 * offering-type colour carried by a tinted header band (eyebrow + status pill);
 * title, inline teacher, meta chips, and a price footer stack below it.
 * Clicking the card opens the offering detail page; the teacher chip links to
 * the public teacher profile. Logged-in students also see a match-score footer.
 * On the search results page (`showMatchBreakdown`), hovering that footer lazily
 * fetches and reveals the per-dimension "Why this match?" breakdown.
 */
export function OfferingCard({
  offering,
  className,
  searchContext,
  showMatchBreakdown = false,
  activeGradeBands,
}: {
  offering: OfferingSearchResult;
  className?: string;
  /** Active search filter snapshot — keeps the breakdown consistent with the ranked score. */
  searchContext?: SearchContextSnapshot | null;
  /** Enable the hover breakdown popover (search results page only). */
  showMatchBreakdown?: boolean;
  /**
   * Grade bands currently selected in the search filters, if any. When set, the
   * card's grade chip shows whichever of the offering's grade bands actually
   * matched the filter, rather than always the first one — a multi-grade
   * offering (e.g. Grade 7 & 11) that matched on Grade 7 would otherwise show
   * "Grade 11" and read as if the filter weren't working.
   */
  activeGradeBands?: string[];
}) {
  const navigate = useNavigate();
  const gradeBands = useGradeBands();
  const { getToken } = useAuth();
  const theme = OFFERING_TYPE_THEME[offering.offeringType];
  const Icon = theme.icon;

  // Lazy per-offering breakdown, fetched on first hover and cached until the
  // search context changes (which alters the ranking inputs, so we re-fetch).
  const [breakdown, setBreakdown] = useState<MatchScoreBreakdown | null>(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    fetchedRef.current = null;
  }, [searchContext]);

  const handleOpenChange = useCallback(
    async (open: boolean) => {
      if (!open || fetchedRef.current === offering.offeringId) return;
      setLoadingBreakdown(true);
      try {
        const token = await getToken();
        if (!token) return;
        const data = await getOfferingMatchScore(offering.offeringId, token, searchContext);
        setBreakdown(data);
        fetchedRef.current = offering.offeringId;
      } catch {
        // silently ignore — the popover shows an "unavailable" state
      } finally {
        setLoadingBreakdown(false);
      }
    },
    [offering.offeringId, getToken, searchContext],
  );

  // Prefer whichever grade band(s) actually matched the active filter over
  // always showing the first one (see `activeGradeBands` doc above). With no
  // active filter, keep the original single-first-grade-band display.
  const matchedGradeBands = activeGradeBands?.length
    ? (offering.gradeBands ?? []).filter(g => activeGradeBands.includes(g))
    : [];
  const gradeBandsToShow = matchedGradeBands.length ? matchedGradeBands : offering.gradeBands?.slice(0, 1);
  const gradeLabel = gradeBandsToShow?.length
    ? gradeBandsToShow.map(g => gradeBands.labelOf(g)).join(", ")
    : null;

  const startDate =
    offering.offeringType === "GROUP" ? offering.group?.nextBatchStartDate ?? null
    : offering.offeringType === "CRASH_COURSE" ? offering.crashCourse?.courseStartDate ?? null
    : null;
  const inProgress = startDate != null && daysUntil(startDate) <= 0;

  const sym = currencySymbol(offering.currency ?? undefined);
  const priceText = formatPriceRange(offering.minPrice, offering.maxPrice, sym);
  const hasDiscount =
    offering.minEarlyBirdPrice != null && offering.minPrice != null && offering.minEarlyBirdPrice < offering.minPrice;
  // Search prices are per-session (ADR-088). Keep "/session" for every type — a
  // low group/crash figure labelled "/course" would misrepresent the price.
  const bigPrice = hasDiscount ? `${sym}${fmt(offering.minEarlyBirdPrice!)}` : priceText;
  const discountPct = hasDiscount
    ? Math.round(((offering.minPrice! - offering.minEarlyBirdPrice!) / offering.minPrice!) * 100)
    : null;
  const priceNote = offeringPriceNote(offering, discountPct);

  const statusPill = offeringStatusPill(offering, startDate, inProgress);
  const meta = offeringMeta(offering, gradeLabel);
  const distance = formatDistance(offering.distanceKm);

  const goTeacher = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (offering.teacherHandle) navigate(`/t/${offering.teacherHandle}`);
  };

  return (
    <Link
      to={offering.teacherHandle && offering.slug
        ? `/t/${offering.teacherHandle}/${offering.slug}`
        : `/public/offering/${offering.offeringId}`}
      className={cn(
        "flex flex-col w-full rounded-2xl bg-card overflow-hidden shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        className,
      )}
    >
      {/* Tinted header band — the type-colour surface: eyebrow + status pill */}
      <div className={cn("px-4 py-2.5 bg-gradient-to-r border-b", theme.headerBg)}>
        <div className="flex items-center justify-between gap-2.5">
          <span className={cn("inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.05em] min-w-0", theme.accent)}>
            <span className={cn("inline-grid place-items-center w-[22px] h-[22px] rounded-md shrink-0 bg-white/70 dark:bg-white/10 ring-1 ring-black/5", theme.accent)}>
              <Icon className="w-3.5 h-3.5" />
            </span>
            <span className="truncate">
              {offering.subjectName ? `${offering.subjectName} · ${theme.typeLabel}` : theme.typeLabel}
            </span>
          </span>
          {statusPill && (
            <span
              className={cn(
                "shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap bg-white/80 dark:bg-white/10 ring-1 ring-black/5 shadow-sm",
                statusPill.trial ? "text-emerald-700 dark:text-emerald-300" : theme.accent,
              )}
            >
              <span className={cn("w-1.5 h-1.5 rounded-full", statusPill.trial ? "bg-emerald-500" : theme.dot)} />
              {statusPill.text}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 px-4 pt-3 pb-4 flex flex-col gap-3">
        {/* Row B — title */}
        <h3 className={cn(
          "text-base font-bold leading-snug tracking-tight text-slate-900 dark:text-white line-clamp-2 transition-colors",
          theme.titleHover,
        )}>
          {offering.title}
        </h3>

        {/* Row C — teacher, inline; links to the public teacher profile */}
        <div
          role="link"
          tabIndex={0}
          onClick={goTeacher}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") goTeacher(e); }}
          className="flex items-center gap-2 min-w-0 cursor-pointer group/teacher focus:outline-none"
          title={`View ${offering.teacherName ?? "teacher"}'s profile`}
        >
          <TeacherAvatar url={offering.teacherProfileImageUrl} name={offering.teacherName} />
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate group-hover/teacher:underline">
            {offering.teacherName ?? "Teacher"}
          </span>
          <BadgeCheck className="w-3.5 h-3.5 shrink-0 text-emerald-600" />
          <SubscriptionTierBadge tier={offering.teacherTier} className="shrink-0" />
          {distance && (
            <span className="shrink-0 ml-auto text-xs text-muted-foreground">{distance}</span>
          )}
        </div>

        {/* Row D — meta chips */}
        {meta.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {meta.map((m) => (
              <span
                key={m}
                className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap"
              >
                {m}
              </span>
            ))}
          </div>
        )}

        {/* Aggregate rating (AC9) — RatingBadge renders nothing for zero-review offerings.
            Wrapped so the inline pill sizes to its content: as a direct flex-column child it
            would otherwise stretch full-width (align-items: stretch) instead of being a compact
            badge like the mockup. */}
        <div className="flex">
          <RatingBadge ratingAvg={offering.ratingAvg} ratingCount={offering.ratingCount} />
        </div>

        {/* Row E — price */}
        <div className="mt-auto pt-3 border-t border-border/50 flex items-end justify-between gap-2.5">
          <div className="min-w-0">
            {bigPrice ? (
              <div className="flex items-baseline gap-1.5 flex-wrap">
                <span className="flex items-baseline gap-1 whitespace-nowrap">
                  <span className="text-lg font-bold tracking-tight text-foreground">{bigPrice}</span>
                  <span className="text-xs text-muted-foreground">/session</span>
                </span>
                {hasDiscount && priceText && (
                  <span className="text-xs text-muted-foreground line-through whitespace-nowrap">{priceText}</span>
                )}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">Contact for price</span>
            )}
          </div>
          {priceNote && (
            <span className={cn("shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap", theme.pill)}>
              {priceNote}
            </span>
          )}
        </div>

        {MATCH_SCORE_UI_ENABLED && offering.matchScore != null &&
          (showMatchBreakdown ? (
            <HoverCard openDelay={100} closeDelay={100} onOpenChange={handleOpenChange}>
              <HoverCardTrigger asChild>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Match score ${offering.matchScore} percent — show the breakdown`}
                  onClick={stopCardNav}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.preventDefault(); }}
                  className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between cursor-help rounded-b-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="text-xs text-muted-foreground">Match</span>
                  <span className="text-sm font-semibold text-[hsl(173,55%,32%)]">{offering.matchScore}%</span>
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                align="end"
                sideOffset={6}
                onClick={stopCardNav}
                className="w-80 p-0 border-0 bg-transparent shadow-none"
              >
                {loadingBreakdown ? (
                  <BreakdownNotice>Loading breakdown…</BreakdownNotice>
                ) : breakdown ? (
                  <div className="rounded-xl overflow-hidden shadow-xl">
                    <WhyThisMatch score={toMatchScore(breakdown)} />
                  </div>
                ) : (
                  <BreakdownNotice>Breakdown unavailable</BreakdownNotice>
                )}
              </HoverCardContent>
            </HoverCard>
          ) : (
            <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Match</span>
              <span className="text-sm font-semibold text-[hsl(173,55%,32%)]">{offering.matchScore}%</span>
            </div>
          ))}
      </div>
    </Link>
  );
}

/** Stop a click inside the score footer / popover from following the card's link. */
function stopCardNav(e: React.SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

/** Small centered notice shown in the breakdown popover while loading or on failure. */
function BreakdownNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-4 text-center text-xs text-slate-400 shadow-xl">
      {children}
    </div>
  );
}

/** Map the API breakdown DTO to the WhyThisMatch view model. */
function toMatchScore(b: MatchScoreBreakdown): MatchScore {
  const map = (d: MatchScoreBreakdown["dimensions"][number]) => ({
    key: d.key,
    label: d.label,
    points: d.points,
    max: d.max,
    result: d.result,
    detail: d.detail,
  });
  return {
    total: b.total,
    maxTotal: b.maxTotal,
    band: b.band as MatchBand,
    dimensions: b.dimensions.map(map),
    nonScored: (b.nonScored ?? []).map(map),
  };
}

/** Teacher avatar — falls back to navy initials if no image or the image fails to load. */
function TeacherAvatar({ url, name }: { url: string | null; name: string | null }) {
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        onError={() => setFailed(true)}
        className="w-[26px] h-[26px] shrink-0 rounded-full object-cover ring-2 ring-white dark:ring-slate-800 shadow-sm"
      />
    );
  }
  return (
    <span className="w-[26px] h-[26px] shrink-0 rounded-full bg-[hsl(210,60%,18%)] flex items-center justify-center text-xs font-bold text-white">
      {initials(name)}
    </span>
  );
}

/**
 * The secondary price-line note. Prefers a discount call-out; otherwise a
 * type-specific descriptor (batch size / total sessions) when available.
 */
function offeringPriceNote(o: OfferingSearchResult, discountPct: number | null): string | null {
  if (o.offeringType === "GROUP" && o.group?.batchSizeMax) return `Batch of ${o.group.batchSizeMax}`;
  if (o.offeringType === "CRASH_COURSE" && o.crashCourse?.totalSessions) {
    const n = o.crashCourse.totalSessions;
    return `${n} session${n === 1 ? "" : "s"}`;
  }
  if (discountPct != null && discountPct > 0) return `Save ${discountPct}%`;
  return null;
}

/**
 * The single status pill. Prefers a concrete start signal (batch/course start)
 * over the trial flag; falls back to "Free trial" when no date is known.
 * There is no enrolment data, so "seats left" cannot be shown.
 */
function offeringStatusPill(
  o: OfferingSearchResult,
  startDate: string | null,
  inProgress: boolean,
): { text: string; trial: boolean } | null {
  if (startDate) return { text: inProgress ? "In progress" : `Starts ${formatShortDate(startDate)}`, trial: false };
  if (o.trialSessionAvailable) return { text: "Free trial", trial: true };
  return null;
}

/** Up to three real meta facts, type-appropriate, in priority order. */
function offeringMeta(o: OfferingSearchResult, gradeLabel: string | null): string[] {
  const facts: (string | null)[] = [];
  if (o.offeringType === "ONE_TO_ONE") {
    const d = o.oneToOne?.sessionDurationMinutes;
    facts.push(d ? `${d} min` : null, teachingModeLabel(o.teachingMode), gradeLabel);
  } else if (o.offeringType === "GROUP") {
    const spw = o.group?.sessionsPerWeek;
    const d = o.group?.sessionDurationMinutes;
    facts.push(spw ? `${spw}×/week` : null, teachingModeLabel(o.teachingMode), d ? `${d} min` : null, gradeLabel);
  } else {
    const m = o.crashCourse?.mockTestsIncluded;
    const d = o.crashCourse?.sessionDurationMinutes;
    facts.push(teachingModeLabel(o.teachingMode), m ? `${m} mock test${m === 1 ? "" : "s"}` : null, d ? `${d} min` : null, gradeLabel);
  }
  return facts.filter((f): f is string => Boolean(f)).slice(0, 3);
}

function formatDistance(km: number | null): string | null {
  if (km == null) return null;
  return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
}

function initials(name: string | null): string {
  if (!name) return "T";
  return name.split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

function teachingModeLabel(mode: string | null): string | null {
  switch (mode) {
    case "ONLINE": return "Online";
    case "OFFLINE": return "Offline";
    case "HYBRID": return "Hybrid";
    default: return null;
  }
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function formatPriceRange(min: number | null, max: number | null, sym: string): string | null {
  if (min == null && max == null) return null;
  if (min != null && max != null && min !== max) return `${sym}${fmt(min)}–${sym}${fmt(max)}`;
  const v = max ?? min;
  return v != null ? `${sym}${fmt(v)}` : null;
}

function fmt(n: number): string {
  // Per-session prices are often fractional (course total ÷ sessions), so keep
  // two decimals rather than rounding to a misleadingly clean whole number.
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
